import React from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  clearBillingPortalSyncPending,
  hasBillingPortalSyncPending,
  listMyInvoices,
  listMySubscriptionsSummary,
  openStripeBillingPortal,
  syncBillingPortalReturn,
  type InvoiceItem,
  type SubSummary,
} from '../services/billingService'
import { Card, CardContent } from '@/shared/ui/Card'
import { Table, Thead, Tbody, Tr, Th, Td } from '@/shared/ui/Table'
import { Badge, Pagination, Button } from '@/shared/ui'
import { useToast } from '@/shared/ui/Toast'
import { getBillingManageMode, type BillingManageMode } from '@/modules/settings/services/settingsService'
import { useAuth } from '@/modules/auth/context/AuthContext'

function fmtMoney(cents: number, currency?: string | null) {
  try { return new Intl.NumberFormat('pt-BR', { style:'currency', currency: (currency||'BRL').toUpperCase() }).format((cents||0)/100) } catch { return `R$ ${((cents||0)/100).toFixed(2)}` }
}
function fmtDate(iso?: string | null) { return iso ? (new Date(iso).toISOString().replace('T',' ').slice(0,19) + ' UTC') : '-' }
function fmtInterval(iv?: 'month'|'year'|null) { return iv === 'year' ? 'Anual' : iv === 'month' ? 'Mensal' : '-' }

export default function MemberInvoicesPage() {
  const { user } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const [subs, setSubs] = React.useState<SubSummary[]>([])
  const [items, setItems] = React.useState<InvoiceItem[]>([])
  const [paidMap, setPaidMap] = React.useState<Record<string, { amount: number; currency: string|null; account_id?: string|null }>>({})
  const [loading, setLoading] = React.useState(true)
  const [err, setErr] = React.useState<string | null>(null)
  const { add: toast } = useToast()
  const [page, setPage] = React.useState(1)
  const pageSize = 25
  const [manageMode, setManageMode] = React.useState<BillingManageMode>('portal')
  const [openingPortal, setOpeningPortal] = React.useState(false)
  const portalReturnFlag = searchParams.get('portal_return') === '1'

  const openPortal = React.useCallback(async () => {
    try {
      setOpeningPortal(true)
      const returnUrl = (() => {
        if (typeof window === 'undefined') return undefined
        const url = new URL(window.location.href)
        url.searchParams.set('portal_return', '1')
        return url.toString()
      })()
      await openStripeBillingPortal(returnUrl)
    } catch (e: any) {
      toast({ title:'Stripe', description: e?.message || 'Falha ao abrir o portal', variant:'error', soft:true })
    } finally {
      setOpeningPortal(false)
    }
  }, [toast])

  const reload = React.useCallback(async () => {
    setLoading(true)
    try {
      setErr(null)
      const [s, inv] = await Promise.all([listMySubscriptionsSummary(), listMyInvoices()])
      setSubs(s)
      setItems(inv)
      // Monta mapa de valor pago na primeira fatura por assinatura (subscription_id)
      const map: Record<string, { amount: number; currency: string|null; account_id?: string|null }> = {}
      const ordered = [...inv].sort((a,b)=> String(b.created_at||'').localeCompare(String(a.created_at||'')))
      for (const f of ordered) {
        const sid = f.subscription_id || ''
        if (!sid || map[sid]) continue
        const st = String(f.status||'').toLowerCase()
        if (st === 'paid' && (f.amount_paid_cents||0) > 0) {
          map[sid] = { amount: f.amount_paid_cents, currency: f.currency || null, account_id: (f as any).account_id || null }
        }
      }
      setPaidMap(map)
    } catch (e: any) {
      const msg = e?.message || 'Falha ao carregar faturas'
      setErr(msg)
      toast({ title: 'Assinatura', description: msg, variant: 'error', soft: true })
    } finally {
      setLoading(false)
    }
  }, [toast])

  const clearPortalReturnParam = React.useCallback(() => {
    if (!portalReturnFlag) return
    const next = new URLSearchParams(searchParams)
    next.delete('portal_return')
    setSearchParams(next, { replace: true })
  }, [portalReturnFlag, searchParams, setSearchParams])

  const syncAfterPortalReturn = React.useCallback(async () => {
    const userId = String(user?.id || '').trim()
    const shouldSync = portalReturnFlag || hasBillingPortalSyncPending()
    if (!userId || !shouldSync) return
    try {
      const synced = await syncBillingPortalReturn(userId)
      if (synced) {
        await reload()
        toast({
          title: 'Assinatura',
          description: 'Assinatura sincronizada com o Stripe.',
          variant: 'info',
          soft: true,
        })
      } else {
        clearBillingPortalSyncPending()
      }
    } finally {
      clearPortalReturnParam()
    }
  }, [clearPortalReturnParam, portalReturnFlag, reload, toast, user?.id])

  React.useEffect(() => { reload() }, [reload])
  React.useEffect(() => { (async () => { try { setManageMode(await getBillingManageMode()) } catch {} })() }, [])
  React.useEffect(() => { void syncAfterPortalReturn() }, [syncAfterPortalReturn])
  React.useEffect(() => {
    if (!user?.id) return
    const onFocus = () => { void syncAfterPortalReturn() }
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        void syncAfterPortalReturn()
      }
    }
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [syncAfterPortalReturn, user?.id])

  const totalPages = Math.max(1, Math.ceil((items?.length || 0) / pageSize))
  const start = (page - 1) * pageSize
  const pageItems = items.slice(start, start + pageSize)

  return (
    <div className="mx-auto w-full max-w-[1150px]">
      <div className="mb-6 text-center">
        <div className="text-xs uppercase tracking-wider text-foreground/70">Assinatura</div>
        <h1 className="mt-1 text-2xl font-semibold">Histórico de faturas</h1>
      </div>

      <Card className="mb-6">
        <CardContent>
          <div className="mb-3 flex items-center justify-between">
            <div className="text-sm font-medium">Assinaturas</div>
            {manageMode === 'portal' && (
              <Button
                className="h-8 px-3 py-1 text-xs"
                disabled={openingPortal}
                onClick={openPortal}
              >{openingPortal ? 'Abrindo Portal...' : 'Gerenciar cartão'}</Button>
            )}
          </div>
          <Table>
            <Thead>
              <Tr>
                <Th>Plano</Th>
                <Th>Valor</Th>
                <Th>Intervalo</Th>
                <Th>Status</Th>
                <Th>Início</Th>
                <Th>Próximo ciclo</Th>
              </Tr>
            </Thead>
            <Tbody>
              {subs.map((s) => (
                <Tr key={s.id}>
                  <Td>{s.plan_name || '-'}</Td>
                  <Td>{(() => {
                    const sid = s.stripe_subscription_id || ''
                    let paid = sid ? paidMap[sid] : undefined
                    // Fallback: usar primeira fatura paga do mesmo account_id
                    if (!paid && s.account_id) {
                      const firstForAccount = Object.values(paidMap).find((x) => (x.account_id || null) === s.account_id)
                      if (firstForAccount) paid = firstForAccount
                    }
                    const cents = paid ? paid.amount : (s.amount_cents || 0)
                    const cur = paid ? (paid.currency || s.currency) : s.currency
                    return fmtMoney(cents, cur)
                  })()}</Td>
                  <Td>{fmtInterval(s.interval)}</Td>
                  <Td>
                    {(() => {
                      const raw = String(s.status || '').toLowerCase()
                      const map: Record<string,string> = {
                        active: 'Ativo',
                        trialing: 'Em teste',
                        past_due: 'Em atraso',
                        canceled: 'Cancelada',
                        incomplete: 'Incompleta',
                        unpaid: 'Não pago',
                        paused: 'Pausada',
                      }
                      const label = map[raw] || (raw ? raw : '-')
                      const color = raw === 'active' ? 'success' : raw === 'trialing' ? 'info' : raw === 'past_due' ? 'warning' : raw === 'canceled' ? 'error' : 'default'
                      return <Badge variant="soft" color={color as any} className="capitalize">{label}</Badge>
                    })()}
                  </Td>
                  <Td>{fmtDate(s.created_at)}</Td>
                  <Td>{fmtDate(s.current_period_end)}</Td>
                </Tr>
              ))}
              {subs.length===0 && (
                <Tr><Td colSpan={6} className="p-6 text-center text-sm text-foreground/70">{loading ? 'Carregando…' : 'Sem assinaturas para exibir.'}</Td></Tr>
              )}
            </Tbody>
          </Table>
        </CardContent>
      </Card>

      {/* Card de faturas removido conforme solicitação. O resumo acima é a fonte oficial. */}
    </div>
  )
}









